import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { forkJoin, iif, Observable, of, throwError } from 'rxjs';
import {
  catchError,
  concat,
  filter,
  flatMap,
  map,
  mergeMap,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs/operators';
import { EpisodeOfCare } from '@interfaces/episode-of-care.interface';
import { EpisodeOfCareComponent } from '@interfaces/episode-of-care-component.interface';
import { SearchSummaryService } from '../search/search.summary.service';
import { SearchParams } from '@interfaces/search-params.model';
import { TranslateService } from '@ngx-translate/core';
import { Provider } from '@interfaces/provider.model';
import { EpisodeOfCareTotals } from '@interfaces/episode-of-care-totals.model';
import { EpisodeOfCareProviderSummary } from '@interfaces/episode-of-care-provider-summary.interface';
import { EpisodeOfCareCriticalParams } from '@interfaces/episode-of-care-critical-params.model';
import { EpisodeOfCareAverageCost } from '@interfaces/episode-of-care-average-cost.interface';
import { getIncentiveAmount } from '../../utilities/provider.utilities';
import { WindowService } from '../window.service';
import { EpisodeOfCareCarepaths } from '@interfaces/episode-of-care-carepaths.interface';
import { EpisodeOfCareCarepathSteps } from '@interfaces/episode-of-care-carepath-steps.interface';
import { EpisodeOfCareCarepathChangeTo } from '@interfaces/episode-of-care-carepath-change-to.model';
import { IntendedProviderService } from '../intended-provider/intended-provider.service';
import { IntendedProvider } from '@interfaces/intended-provider.model';
import { ObservableError } from '@interfaces/observable-error.model';
import { ProceduresService } from '../procedures.service';
import { EpisodeOfCareCarepathLocations } from '@interfaces/episode-of-care-carepath-locations.interface';
import {
  cloneDeep,
  find,
  some,
  filter as _filter,
  uniqBy,
  each,
  sortBy,
  clone,
  map as _map,
} from 'lodash';

const DEFAULT_PROCEDURE_SEARCH_RADIUS = '25';

@Injectable({
  providedIn: 'root',
})
export class EpisodesOfCareService {
  private episodeCache: {
    [serializedParams: string]: Observable<EpisodeOfCare>;
  } = {};
  private providerCache: {
    [serializedParams: string]: Observable<EpisodeOfCareProviderSummary>;
  } = {};
  private sessionStorageKeys = {
    carepath: 'my_carepath',
    primaryProviderChangeNoticeSuppress:
      'my_carepath_primary_change_notice_suppress',
    secondaryProviderChangeNoticeSuppress:
      'my_carepath_secondary_change_notice_suppress',
    carepathLocations: 'my_carepath_locations',
  };

  constructor(
    private http: HttpClient,
    private searchSummary: SearchSummaryService,
    private translate: TranslateService,
    private windowService: WindowService,
    private proceduresService: ProceduresService,
    private intendedProviders: IntendedProviderService
  ) {}

  /**
   * Get an episode of care for a procedure_id.
   * Episode provider data will load/prefetch async-ly.
   * Data is cached by procedure id and params.
   * Provider swap flow is also handled here.
   * @param procedureId: number
   * @param params: SearchParams
   * @param change?: EpisodeOfCareCarepathChangeTo
   * @returns Observable<EpisodeOfCare>
   *   Error Codes:
   *    - NO_EPISODE: No episode found for procedure.
   *    - NO_PRIMARY_PROVIDER: No professional or facility provider found for primary procedure.
   *    - NO_PRIMARY_PROFESSIONAL: No professional provider found for primary procedure.
   */
  public getEpisodeOfCare(
    procedureId: number,
    params: SearchParams,
    change?: EpisodeOfCareCarepathChangeTo
  ): Observable<EpisodeOfCare> {
    if (!change) {
      const cached = this.cacheGet(
        this.episodeCache,
        procedureId,
        params
      ) as Observable<EpisodeOfCare>;
      if (cached) {
        return cached;
      }
    }
    const toCache = this.requestEpisodeOfCare(procedureId).pipe(
      tap((episode: EpisodeOfCare) =>
        this.updateCarepathStepsWithChange(episode, change)
      ),
      switchMap((episode: EpisodeOfCare) =>
        of(cloneDeep(episode)).pipe(
          concat(this.getEpisodeProviders(episode, params))
        )
      ),
      filter((episode: EpisodeOfCare) => episode !== undefined),
      tap((episode: EpisodeOfCare) => this.storeEpisodeCarepathSteps(episode)) // Store provider selection
    );
    return this.cacheSet(
      this.episodeCache,
      toCache,
      procedureId,
      params
    ) as Observable<EpisodeOfCare>;
  }

  /**
   * Get all available episodes of care.
   * @returns Observable<EpisodeOfCare[]>
   */
  public getEpisodesOfCare(): Observable<EpisodeOfCare[]> {
    return this.requestEpisodesOfCare();
  }

  /**
   * Get similar procedures for a procedure_id.
   * @param procedureId: number
   * @returns Observable<EpisodeOfCareComponent[]>
   */
  public getSimilarProcedures(
    procedureId: number
  ): Observable<EpisodeOfCareComponent[]> {
    return this.requestEpisodesOfCare(procedureId).pipe(
      map((episode) => this.handleSimilarProcedures(episode[0]))
    );
  }

  /**
   * Get similar procedures from an episode of care.
   * @param episode: EpisodeOfCare
   * @returns EpisodeOfCareComponent[]
   */
  public getSimilarProceduresFromEpisode(
    episode: EpisodeOfCare
  ): EpisodeOfCareComponent[] {
    return this.handleSimilarProcedures(episode);
  }

  /**
   * Get cost and incentive totals for an episode of care.
   * @param episode: EpisodeOfCare
   * @returns EpisodeOfCareTotals
   */
  public getEpisodeTotals(episode: EpisodeOfCare): EpisodeOfCareTotals {
    return new EpisodeOfCareTotals(episode);
  }

  /**
   * Get an episode of care component by step.
   * @param procedureId: number
   * @param params: SearchParams
   * @param step: number
   * @returns Observable<EpisodeOfCareComponent>
   */
  public getEpisodeComponent(
    procedureId: number,
    params: SearchParams,
    step: number
  ): Observable<EpisodeOfCareComponent> {
    return this.getEpisodeOfCare(procedureId, params).pipe(
      map((episode: EpisodeOfCare) => find(episode.components, { step: step }))
    );
  }

  /**
   * Get primary procedure average cost.
   * @param episode: EpisodeOfCare
   * @param params: SearchParams
   * @returns Observable<EpisodeOfCareAverageCost>
   */
  public getPrimaryProcedureAverageCost(
    episode: EpisodeOfCare,
    params: SearchParams
  ): Observable<EpisodeOfCareAverageCost> {
    return this.requestProviderSummary(
      episode.primary_procedure_id,
      params
    ).pipe(
      map(
        (summary: EpisodeOfCareProviderSummary) =>
          (summary &&
            summary.provider_meta &&
            summary.provider_meta.cost &&
            summary.provider_meta.cost.average &&
            summary.provider_meta.cost.average.procedure_cost) ||
          null
      )
    );
  }

  /**
   * Checks if episode has any incentivized procedures.
   * @param episode: EpisodeOfCare
   * @returns boolean
   */
  public episodeHasIncentives(episode: EpisodeOfCare): boolean {
    return some(episode.components, this.episodeComponentHasIncentive);
  }

  /** Check if Carepath has been customized.
   * @param procedureId: number
   * @returns boolean
   */
  public isCustomCarepath(procedureId: number): boolean {
    const carepath = this.carepathStorageGetSteps(procedureId);
    return some(carepath, { custom: true });
  }

  /**
   * Get Carepath change notice suppression.
   * @param primary: boolean
   * @returns boolean
   */
  public isCarepathChangeNoticeSuppressed(primary: boolean): boolean {
    const key = this.getProviderChangeSuppressKey(primary);
    return this.windowService['sessionStorage'].getItem(key) === 'true';
  }

  /**
   * Set Carepath change notice as suppressed.
   * @param primary: boolean
   * @returns void
   */
  public suppressCarepathChangeNotice(primary: boolean): void {
    const key = this.getProviderChangeSuppressKey(primary);
    this.windowService['sessionStorage'].setItem(key, 'true');
  }

  /**
   * Check if episode component is primary.
   * @param component: EpisodeOfCareComponent
   * @returns boolean
   */
  public isPrimaryComponent(component: EpisodeOfCareComponent): boolean {
    return !!(component && component.type_code === 'SURG');
  }

  /**
   * Check if episode component is an office visit.
   * @param component: EpisodeOfCareComponent
   * @returns boolean
   */
  public isOfficeVisitComponent(component: EpisodeOfCareComponent): boolean {
    return !!(component && component.x12_code);
  }

  /**
   * Get intended providers.
   * @returns Observable<IntendedProvider[]>
   */
  public getIntendedProviders(params): Observable<IntendedProvider[]> {
    const requestParams = new SearchParams(params);
    return this.intendedProviders
      .getProviders(requestParams)
      .pipe(shareReplay(1));
  }

  /** Get primary component from episode.
   * @param episode: EpisodeOfCare
   * @returns EpisodeOfCareComponent
   */
  public getPrimaryComponent(episode: EpisodeOfCare): EpisodeOfCareComponent {
    return find(episode.components, { type_code: 'SURG' });
  }

  /**
   * Get office visit components from episode.
   * @param episode: EpisodeOfCare
   * @returns EpisodeOfCareComponent[]
   */
  public getOfficeVisitComponents(
    episode: EpisodeOfCare
  ): EpisodeOfCareComponent[] {
    return _filter(
      episode.components,
      (comp: EpisodeOfCareComponent) => !!comp.x12_code
    );
  }

  /**
   * Check if episode has office visits.
   * @param episode: EpisodeOfCare
   * @returns boolean
   */
  public hasOfficeVisitProviders(episode: EpisodeOfCare): boolean {
    const comps = this.getOfficeVisitComponents(episode);
    return (
      _filter(comps, (comp: EpisodeOfCareComponent) => !!comp.provider).length >
      0
    );
  }

  /**
   * Get episode component by step.
   * Step = 0: Primary component
   * @param episode: EpisodeOfCare
   * @param step: number
   * @returns EpisodeOfCareComponent
   */
  public getComponentByStep(
    episode: EpisodeOfCare,
    step: number
  ): EpisodeOfCareComponent {
    if (step === 0) {
      return this.getPrimaryComponent(episode);
    }
    return find(episode.components, { step: step });
  }

  /**
   * Get Carepath saved status.
   * @param procedureId: number
   * @returns boolean
   */
  public isCarepathSaved(procedureId: number): boolean {
    return !!this.carepathStorageGetSteps(procedureId)[1];
  }

  /**
   * Get status of Carepath location change.
   * Returns true if current location does not match saved location.
   * @param procedureId number
   * @param location string
   * @returns boolean
   */
  public hasCarepathLocationChanged(
    procedureId: number,
    location: string
  ): boolean {
    const savedLocation = this.carepathStorageGetLocations()[procedureId];
    return savedLocation && location && savedLocation !== location;
  }

  /**
   * Set Carepath location.
   * @param procedureId number
   * @param location string
   */
  public setCarepathLocation(procedureId: number, location: string): void {
    this.carepathStorageSetLocation(procedureId, location);
  }

  /**
   * Reset Carepath for a given procedure ID.
   * @param procedureId: number
   */
  public resetCarepath(procedureId: number): void {
    const storage = this.carepathStorageGet();
    if (storage && storage[procedureId]) {
      this.carepathStorageSetSteps(procedureId, null);
    }
    this.cacheClearEpisode(procedureId);
  }

  private getEpisodeProviders(
    episode: EpisodeOfCare,
    params: SearchParams
  ): Observable<EpisodeOfCare> {
    const primaryComp = this.getPrimaryComponent(episode);
    const summaryParams = this.setCustomCarepathStepSelectionParams(
      episode.primary_procedure_id,
      primaryComp.step,
      params
    );
    return this.requestProviderSummary(
      episode.primary_procedure_id,
      summaryParams
    ).pipe(
      mergeMap((summary: EpisodeOfCareProviderSummary) => {
        return iif(
          () => !!summary.provider,
          iif(
            () => this.isProfessionalProvider(summary.provider),
            forkJoin(
              this.getEpisodeProviderRequests(
                episode,
                summary && summary.provider,
                params
              )
            ),
            throwError(
              new ObservableError({ message: 'NO_PRIMARY_PROFESSIONAL' })
            )
          ),
          throwError(new ObservableError({ message: 'NO_PRIMARY_PROVIDER' }))
        );
      }),
      tap(() => this.setEpisodeProvidersAsLoaded(episode)),
      map(() => cloneDeep(episode))
    );
  }

  private updateCarepathStepsWithChange(
    episode: EpisodeOfCare,
    change: EpisodeOfCareCarepathChangeTo
  ): void {
    if (!episode || !change) {
      return;
    }
    change = this.validateChange(change, episode);
    const carepath = this.carepathStorageGetSteps(episode.primary_procedure_id);
    const compToChange = this.getComponentByStep(episode, change.step);
    this.updateCarepathWithCustomChange(carepath, change);
    if (this.isPrimaryComponent(compToChange)) {
      this.updateCarepathAsPrimaryComponent(carepath, episode, change);
    } else if (this.isOfficeVisitComponent(compToChange)) {
      this.updateCarepathAsOfficeVisitComponent(carepath, episode, change);
    }
    this.carepathStorageSetSteps(episode.primary_procedure_id, carepath);
  }

  private requestEpisodesOfCare(
    procedureId?: number
  ): Observable<EpisodeOfCare[]> {
    const url = `/api/episodes_of_care.json`;
    const params: any = {
      locale: this.getLocale(),
    };
    if (procedureId) {
      params.procedure_id = procedureId;
    }
    return this.http.get(url, { withCredentials: true, params: params }).pipe(
      catchError(() => of(null)),
      map((response: any) => this.handleEpisodesOfCareResponse(response))
    );
  }

  private requestEpisodeOfCare(procedureId: number): Observable<EpisodeOfCare> {
    return this.requestEpisodesOfCare(procedureId).pipe(
      map((episodes: EpisodeOfCare[]) => (episodes && episodes[0]) || null),
      mergeMap((episode: EpisodeOfCare) =>
        iif(
          () => !!episode,
          of(episode),
          throwError(new ObservableError({ message: 'NO_EPISODE' }))
        )
      ),
      tap((episode) => this.setEpisodeProvidersAsLoaded(episode, false)),
      shareReplay(1) // Return last result without making new HTTP calls.
    );
  }

  private requestProviderSummary(
    procedureId: number,
    params: SearchParams
  ): Observable<EpisodeOfCareProviderSummary> {
    const cache = this.cacheGet(
      this.providerCache,
      procedureId,
      params
    ) as Observable<EpisodeOfCareProviderSummary>;
    if (cache) {
      return cache;
    }

    const procedure$ = this.proceduresService
      .requestIncentivizedProcedure(procedureId.toString())
      .pipe(
        catchError(() => of(null)), // 404 from incentivized procedure endpoint
        flatMap((procedure) => {
          return this.searchSummary.getSearchSummary(
            this.providerSummaryParams(params, procedureId, procedure)
          );
        }),
        map((resp) => {
          return {
            provider_meta: (resp && resp._meta) || null,
            provider: (resp && resp.providers && resp.providers[0]) || null,
          };
        }),
        shareReplay(1) // Caching. Return last result without making new HTTP calls.
      );

    return this.cacheSet(
      this.providerCache,
      procedure$,
      procedureId,
      params
    ) as Observable<EpisodeOfCareProviderSummary>;
  }

  private providerSummaryParams(
    params: SearchParams,
    procedureId: number,
    procedure
  ) {
    const summaryParams = new SearchParams(params);

    if (!params.x12_code) {
      summaryParams.procedure_id = `${procedureId}`;
    }

    summaryParams.cache = 'false';
    summaryParams.page = '1';
    summaryParams.limit = '1';
    summaryParams.radius = this.procedureSearchRadius(procedure);
    return summaryParams;
  }

  private getEpisodeProviderRequests(
    episode: EpisodeOfCare,
    primaryProvider: Provider,
    params: SearchParams
  ): Observable<EpisodeOfCare>[] {
    const providerRequests = [];
    episode.components.forEach((comp) => {
      let summaryParams = new SearchParams(params);
      if (comp.x12_code) {
        summaryParams.x12_code = comp.x12_code;
        if (primaryProvider) {
          summaryParams.client_canonical_id =
            primaryProvider.client_canonical_id;
          summaryParams.provider_id = primaryProvider.provider_id;
        }
      }
      summaryParams = this.setCustomCarepathStepSelectionParams(
        episode.primary_procedure_id,
        comp.step,
        summaryParams
      );
      const request = this.requestAndAugmentEpisodeComponentWithProvider(
        episode,
        comp,
        summaryParams
      );
      providerRequests.push(request);
    });
    return providerRequests;
  }

  private requestAndAugmentEpisodeComponentWithProvider(
    episode: EpisodeOfCare,
    component: EpisodeOfCareComponent,
    params: SearchParams
  ): Observable<EpisodeOfCare> {
    return this.requestProviderSummary(component.procedure_id, params).pipe(
      map((summary: EpisodeOfCareProviderSummary) => {
        component.provider_meta = summary.provider_meta;
        component.provider = summary.provider;
        return episode;
      })
    );
  }

  private handleEpisodesOfCareResponse(response: any): EpisodeOfCare[] {
    if (response && response.episodes_of_care) {
      response.episodes_of_care.map((episode: EpisodeOfCare) => {
        return episode.components.map((comp: EpisodeOfCareComponent) => {
          comp.provider = undefined;
          return comp;
        });
      });
      return response.episodes_of_care;
    }
    return [];
  }

  private setEpisodeProvidersAsLoaded(
    episode: EpisodeOfCare,
    loaded: boolean = true
  ): void {
    episode.providers_loaded = loaded;
  }

  private handleSimilarProcedures(
    episode: EpisodeOfCare
  ): EpisodeOfCareComponent[] {
    if (episode && episode.components) {
      let comps = episode.components.filter(
        (item) => item.procedure_id !== episode.primary_procedure_id
      );
      comps = uniqBy(comps, 'procedure_id');
      return comps;
    }
    return null;
  }

  private procedureSearchRadius(procedure) {
    if (
      procedure &&
      procedure.eligible_for_incentive &&
      procedure.minimum_search_radius
    ) {
      return procedure.minimum_search_radius.toString();
    }

    return DEFAULT_PROCEDURE_SEARCH_RADIUS;
  }

  private getLocale(): string {
    return `${this.translate.currentLang || 'en'}`;
  }

  private cacheGet(
    cache: {
      [serializedParams: string]:
        | Observable<EpisodeOfCare>
        | Observable<EpisodeOfCareProviderSummary>;
    },
    procedureId: number,
    params: SearchParams
  ): Observable<EpisodeOfCare> | Observable<EpisodeOfCareProviderSummary> {
    const key = this.cacheSerializeKey(procedureId, params);
    return cache[key] || null;
  }

  private cacheSet(
    cache: {
      [serializedParams: string]:
        | Observable<EpisodeOfCare>
        | Observable<EpisodeOfCareProviderSummary>;
    },
    value: Observable<EpisodeOfCare> | Observable<EpisodeOfCareProviderSummary>,
    procedureId: number,
    params: SearchParams
  ): Observable<EpisodeOfCare> | Observable<EpisodeOfCareProviderSummary> {
    const key = this.cacheSerializeKey(procedureId, params);
    return (cache[key] = value);
  }

  private cacheClearEpisode(procedureId: number): void {
    each(this.episodeCache, (cache: any, key: string) => {
      if (key.indexOf(String(procedureId)) !== -1) {
        this.episodeCache[key] = null;
      }
    });
  }

  private cacheSerializeKey(procedureId: number, params: SearchParams): string {
    const critParams = new EpisodeOfCareCriticalParams(params);
    critParams.procedure_id = String(procedureId);
    let props: any[] = _map(critParams, (value, key) => ({
      key: key,
      value: value,
    }));
    props = sortBy(props, 'key');
    return JSON.stringify(props);
  }

  private getCarepathStepsFromEpisode(
    episode: EpisodeOfCare
  ): EpisodeOfCareCarepathSteps {
    const steps: EpisodeOfCareCarepathSteps = {};
    const savedSteps = this.carepathStorageGetSteps(
      episode.primary_procedure_id
    );
    if (episode && episode.components) {
      episode.components.forEach((component: EpisodeOfCareComponent) => {
        if (component.provider) {
          const savedStepIsCustom =
            (savedSteps &&
              savedSteps[component.step] &&
              savedSteps[component.step].custom) ||
            false;
          steps[component.step] = {
            provider_id: component.provider.provider_id,
            location_id: component.provider.location_id,
            geo_location:
              (component.provider.cost &&
                component.provider.cost[0] &&
                component.provider.cost[0].postal_code) ||
              component.provider.postal_code,
            custom: savedStepIsCustom,
          };
        }
      });
    }
    return steps;
  }

  private setCustomCarepathStepSelectionParams(
    procedureId: number,
    step: number,
    params: SearchParams
  ): SearchParams {
    params = clone(params);
    const carepath = this.carepathStorageGetSteps(procedureId);
    if (carepath && carepath[step]) {
      if (carepath[step].provider_id) {
        params.provider_id = carepath[step].provider_id;
      }
      if (carepath[step].location_id) {
        params.location_id = String(carepath[step].location_id);
      }
      if (carepath[step].geo_location) {
        params.geo_location = carepath[step].geo_location;
      }
    }
    return params;
  }

  private storeEpisodeCarepathSteps(episode: EpisodeOfCare): void {
    if (!episode || !episode.providers_loaded) {
      return;
    }
    const carepathSteps = this.getCarepathStepsFromEpisode(episode);
    this.carepathStorageSetSteps(episode.primary_procedure_id, carepathSteps);
  }

  private carepathStorageSetSteps(
    procedureId: number,
    carepath: EpisodeOfCareCarepathSteps
  ): void {
    const carepaths: EpisodeOfCareCarepaths = this.carepathStorageGet();
    carepaths[procedureId] = carepath;
    this.windowService['sessionStorage'].setItem(
      this.sessionStorageKeys.carepath,
      JSON.stringify(carepaths)
    );
  }

  private carepathStorageGet(): EpisodeOfCareCarepaths {
    let carepaths: EpisodeOfCareCarepaths;
    const carepathJson = this.windowService['sessionStorage'].getItem(
      this.sessionStorageKeys.carepath
    );
    try {
      carepaths = JSON.parse(carepathJson);
    } catch (e) {
      return;
    }
    return carepaths || {};
  }

  private carepathStorageGetSteps(
    procedureId: number
  ): EpisodeOfCareCarepathSteps {
    const carepaths: EpisodeOfCareCarepaths = this.carepathStorageGet();
    return (carepaths && carepaths[procedureId]) || {};
  }

  private carepathStorageSetLocation(
    procedureId: number,
    location: string
  ): void {
    const locations = this.carepathStorageGetLocations();
    locations[procedureId] = location;
    this.windowService['sessionStorage'].setItem(
      this.sessionStorageKeys.carepathLocations,
      JSON.stringify(locations)
    );
  }

  private carepathStorageGetLocations(): EpisodeOfCareCarepathLocations {
    let locations: EpisodeOfCareCarepathLocations;
    const json = this.windowService['sessionStorage'].getItem(
      this.sessionStorageKeys.carepathLocations
    );
    try {
      locations = JSON.parse(json);
    } catch (e) {
      return;
    }
    return locations || {};
  }

  private updateCarepathWithCustomChange(
    carepath: EpisodeOfCareCarepathSteps,
    change: EpisodeOfCareCarepathChangeTo
  ): void {
    carepath[change.step] = {
      provider_id: change.provider_id,
      location_id: String(change.location_id),
      geo_location: change.geo_location,
      custom: true,
    };
  }

  private updateCarepathAsPrimaryComponent(
    carepath: EpisodeOfCareCarepathSteps,
    episode: EpisodeOfCare,
    change: EpisodeOfCareCarepathChangeTo
  ): void {
    const officeVisits = this.getOfficeVisitComponents(episode);
    if (officeVisits) {
      officeVisits.forEach(
        (comp: EpisodeOfCareComponent) =>
          (carepath[comp.step] = {
            provider_id: change.provider_id,
            location_id: null,
            geo_location: null,
            custom: false,
          })
      );
    }
  }

  private updateCarepathAsOfficeVisitComponent(
    carepath: EpisodeOfCareCarepathSteps,
    episode: EpisodeOfCare,
    change: EpisodeOfCareCarepathChangeTo
  ): void {
    const primaryComp = this.getPrimaryComponent(episode);
    carepath[primaryComp.step] = {
      provider_id: change.provider_id,
      location_id: null,
      geo_location: null,
      custom: false,
    };
    const officeVisits = this.getOfficeVisitComponents(episode);
    officeVisits.forEach((comp: EpisodeOfCareComponent) => {
      carepath[comp.step] = {
        provider_id: change.provider_id,
        location_id: String(change.location_id),
        geo_location: change.geo_location,
        custom: true,
      };
    });
  }

  private validateChange(
    change: EpisodeOfCareCarepathChangeTo,
    episode: EpisodeOfCare
  ): EpisodeOfCareCarepathChangeTo {
    change = clone(change);
    if (change.step === 0) {
      const primary = this.getPrimaryComponent(episode);
      change.step = primary.step;
    }
    return change;
  }

  private isProfessionalProvider(provider: Provider): boolean {
    return !!(provider && provider.provider_type === 'P');
  }

  private getProviderChangeSuppressKey(primary: boolean): string {
    let key = this.sessionStorageKeys.secondaryProviderChangeNoticeSuppress;
    if (primary) {
      key = this.sessionStorageKeys.primaryProviderChangeNoticeSuppress;
    }
    return key;
  }

  private episodeComponentHasIncentive(
    component: EpisodeOfCareComponent
  ): boolean {
    return !!(component.provider && getIncentiveAmount(component.provider));
  }
}
